import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';
import { FaGoogle, FaShieldAlt } from 'react-icons/fa';
import logo_icon_64 from '../../assets/images/logo-icon-64.png';
import { signIn } from '../../domain/services/auth/authService';
import { useUser } from '../../contexts/UserContext';

export default function Login() {
  const { appUser, currentUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/onboarding/banks';

  useEffect(() => {
    console.debug('Login: useEffect: appUser:', appUser);
    console.debug('Login: useEffect: currentUser:', currentUser);
    if (appUser) {
      navigate(from);
    }
  }, [appUser, navigate, currentUser, from]);

  return (
    <>
      <Helmet>
        <title>Login - OmniButler</title>
      </Helmet>
      <section className="md:h-screen py-36 flex items-center bg-gradient-to-b from-indigo-50 to-blue-100 dark:from-slate-900 dark:to-slate-800">
        <div className="container relative">
          <div className="flex justify-center">
            <div className="max-w-[600px] w-full m-auto p-8 bg-white dark:bg-slate-900 shadow-xl rounded-xl">
              <Link to="/">
                <img src={logo_icon_64} className="mx-auto h-16 w-16" alt="OmniButler Logo" />
              </Link>
              <h5 className="my-6 text-2xl font-semibold text-center">Welcome to OmniButler</h5>

              {/* Security Info */}
              <div className="mb-8 text-center">
                <div className="flex items-center justify-center mb-4">
                  {/* Fixed FaShieldAlt component usage */}
                  <FaShieldAlt className="text-indigo-600 mr-2 text-xl" />
                  <span className="text-slate-600 dark:text-slate-300 font-medium">
                    Bank-grade security
                  </span>
                </div>
                <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
                  We use Google Sign-in to ensure the highest level of account security. We never
                  store your passwords or sensitive credentials.
                </p>
              </div>

              {/* What We Access */}
              <ul className="text-sm text-slate-600 dark:text-slate-300 list-disc list-inside space-y-6 mb-10">
                <li className="flex flex-col">
                  <h6 className="font-semibold text-base">Account Verification:</h6>
                  <p className="mt-1">
                    We use your Google email address solely to securely identify and authenticate
                    your OmniButler account. We do not share this information with third parties.
                  </p>
                </li>
                <li className="flex flex-col">
                  <h6 className="font-semibold text-base">Personalized Dashboard:</h6>
                  <p className="mt-1">
                    Your basic profile information (name and profile picture) helps us personalize
                    your dashboard and provide a more welcoming experience. This information is not
                    used for marketing or any other purpose.
                  </p>
                </li>
                <li className="flex flex-col">
                  <h6 className="font-semibold text-base">Connecting Financial Accounts:</h6>
                  <p className="mt-1">
                    We use Plaid, a secure and trusted financial data aggregator, to connect to your
                    bank accounts. We do not store your bank login credentials, and all
                    communication with Plaid is encrypted.
                  </p>
                </li>
                <li className="flex flex-col">
                  <h6 className="font-semibold text-base">Data Privacy:</h6>
                  <p className="mt-1">
                    Your financial data is encrypted both in transit and at rest. We adhere to
                    strict security standards and never share your financial information with third
                    parties.
                  </p>
                </li>
              </ul>

              <form className="text-start">
                <div className="grid grid-cols-1">
                  <div className="mb-4">
                    <button
                      className="w-full py-3 px-5 inline-flex justify-center items-center font-semibold tracking-wide border align-middle duration-500 text-base text-center bg-indigo-600 hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700 text-white rounded-lg"
                      onClick={(e) => {
                        e.preventDefault();
                        signIn();
                      }}
                    >
                      <FaGoogle className="mr-2" /> Continue with Google
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
